import { EntitySharing, EntityType, ResolvedEntity, isShared, isSharedIncoming, sharedAccountDisplayId, sharedAccountDisplayName } from '@frontend/common';
import { ForeignEntityMarker } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactNode } from 'react';
import { Placement } from 'react-bootstrap/esm/types';
import { LuShare2 } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import { PlatformResolvedEntityBadge } from '../../modal/platform-object-badge/platform-resolved-entity-badge.component';
import { MultiEntityButton } from '../multi-entity-button/multi-entity-button.component';

interface ResolvedEntityCellOptions {
    entityTypeOverride?: EntityType;
    emptyValue?: ReactNode;
    className?: string;
    parentIds?: Record<string, string>;
    /** Parent row `shared` — supplies foreign badge tooltip account name. */
    parentShared?: EntitySharing | null;
}

interface ResolvedEntityListCellOptions extends ResolvedEntityCellOptions {
    placement?: Placement;
}

/** Badge for a ResolvedEntity — always forwards `foreign` / account tooltip. */
export const renderResolvedEntityBadge = (entity: ResolvedEntity, options?: ResolvedEntityCellOptions) => (
    <PlatformResolvedEntityBadge
        key={entity.entity_id}
        entity={entity}
        entityType={options?.entityTypeOverride}
        parentIds={options?.parentIds}
        parentShared={options?.parentShared}
    />
);

export const renderResolvedEntityCell = (entity: ResolvedEntity | null | undefined, options?: ResolvedEntityCellOptions) => (
    <td className={options?.className ?? defaultCellClassName}>
        {entity ? renderResolvedEntityBadge(entity, options) : (options?.emptyValue ?? <span>/</span>)}
    </td>
);

export const renderResolvedEntityListCell = (entities: ResolvedEntity[] | null | undefined, options?: ResolvedEntityListCellOptions) => {
    if (!entities || entities.length === 0) {
        return <td className={options?.className ?? defaultCellClassName}>{options?.emptyValue ?? '/'}</td>;
    }

    const renderBadge = (entity: ResolvedEntity) => renderResolvedEntityBadge(entity, options);

    return (
        <td
            key={entities[0].entity_id}
            className={options?.className ?? defaultCellClassName}>
            <div className='flex min-w-0 gap-2.5 overflow-visible text-sm font-medium'>
                {renderBadge(entities[0])}
                {entities.length > 1 && (
                    <MultiEntityButton
                        entities={entities.slice(1)}
                        placement={options?.placement}
                        render={renderBadge}
                    />
                )}
            </div>
        </td>
    );
};

export const renderCategoryCell = (categories: ResolvedEntity[] | null | undefined, options?: Omit<ResolvedEntityListCellOptions, 'entityTypeOverride'>) =>
    renderResolvedEntityListCell(categories, { ...options, entityTypeOverride: EntityType.PRODUCT_CATEGORY });

/** Column id for the optional share indicator (hidden by default via `hideColumns`). */
export const SHARED_COLUMN_ID = 'shared';

/** Stable `hideColumns` array — avoid inline `[SHARED_COLUMN_ID]` (new ref every render). */
export const SHARED_COLUMN_HIDE = [SHARED_COLUMN_ID];

const sharedColumnLabelDescriptor = {
    id: 'platform.components.entity-sharing-section.title',
    description: 'Heading for the sharing block on entity detail modals',
    defaultMessage: 'Sharing'
} as const;

/** Compact header: LuShare2 icon (Columns menu uses `optionsLabel` text). */
export const SharedColumnHeader = () => {
    const intl = useIntl();
    const label = intl.formatMessage(sharedColumnLabelDescriptor);

    return (
        <span
            className='text-foreground-muted inline-flex items-center justify-center'
            title={label}>
            <LuShare2
                className='size-4 shrink-0'
                aria-label={label}
            />
        </span>
    );
};

SharedColumnHeader.optionsLabel = <FormattedMessage {...sharedColumnLabelDescriptor} />;

/** Shared header node for list `headerOverwrite` maps. */
export const SHARED_COLUMN_HEADER = <SharedColumnHeader />;

/** Narrow share-marker cell. Incoming/both → mirrored; outgoing-only → forward. */
export const renderSharedColumnCell = (shared?: EntitySharing | null, options?: { className?: string }) => {
    const showShareMarker = isShared(shared);
    return (
        <td className={options?.className ?? 'text-foreground-default w-10 max-w-10 px-2 py-2 text-center align-middle'}>
            {showShareMarker ? (
                <span className='relative mx-auto inline-flex size-5 items-center justify-center'>
                    <ForeignEntityMarker
                        foreign
                        mirrored={isSharedIncoming(shared)}
                        accountId={sharedAccountDisplayId(shared)}
                        accountName={sharedAccountDisplayName(shared)}
                    />
                </span>
            ) : null}
        </td>
    );
};
